import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiChevronLeft, FiClock, FiLoader, FiMessageSquare, FiSend, FiTrash2 } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import UnauthenticatedMessage from '../components/UnauthenticatedMessage';
import { eventService } from '../services/eventService';
import { getSessionId } from '../services/webviewService';
import { useTitle } from '../contexts/TitleContext';
import { useChats, useDeleteChat, useStarterPrompts, useCreateChat } from '../hooks/useChats';
import { Button } from '../design-system';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { userInfo, loading: authLoading } = useAuth();
  const { setTitle } = useTitle();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  // React Query hooks
  const authenticatedUserId = userInfo?.is_authenticated ? userInfo.user_id : undefined;
  const { data: chats = [], isLoading: loading, error: chatsError } = useChats(authenticatedUserId);
  const { data: starterPrompts = [], isLoading: loadingPrompts } = useStarterPrompts();
  const createChatMutation = useCreateChat(authenticatedUserId);
  const deleteChatMutation = useDeleteChat(authenticatedUserId);
  
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [promptHintMessage, setPromptHintMessage] = useState<string | null>(null);

  useEffect(() => {
    setTitle('دستیار هوشمند سلام');
  }, [setTitle]);

  // Handle chats error
  useEffect(() => {
    if (chatsError) {
      setError('خطا در بارگذاری گفت‌وگوها');
      console.error('Error fetching chats:', chatsError);
    }
  }, [chatsError]);

  // Focus textarea after page loads
  useEffect(() => {
    if (!authLoading && !loading && userInfo?.is_authenticated && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [authLoading, loading, userInfo]);

  // Capture home page view event
  useEffect(() => {
    if (!authLoading && userInfo?.is_authenticated) {
      void eventService.captureHomePageView();
    }
  }, [authLoading, userInfo]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedMessage = inputValue.trim();
    if (!trimmedMessage) {
      return;
    }
    try {
      const sessionId = getSessionId();
      const newChat = await createChatMutation.mutateAsync({
        message: trimmedMessage,
        metadata: { source: 'agent-home' },
        sessionId,
        agent: null,
      });

      // Khabarchin capture is client-side and queued; navigation does not wait for it.
      void eventService.captureSubmitMessage(newChat.id, trimmedMessage, newChat.agent);

      // Navigate to the new chat
      navigate(`/chat/${newChat.id}`);
    } catch (error) {
      console.error('Error creating chat:', error);
      setError('خطا در ایجاد گفت‌وگو');
    }
  };

  const handlePromptClick = (prompt: string) => {
    setInputValue(prompt);
    setPromptHintMessage('نمونه توی کادر قرار گرفت؛ اگه لازم بود جزئیاتش رو تغییر بده.');
    // Auto-hide the message after 5 seconds
    setTimeout(() => setPromptHintMessage(null), 5000);
    // Focus the textarea
    textareaRef.current?.focus();
  };

  const handleDeleteChat = async (chatId: string) => {
    if (!window.confirm('این گفت‌وگو از فهرستت حذف بشه؟')) return;
    try {
      await deleteChatMutation.mutateAsync(Number(chatId));
    } catch (deleteError) {
      console.error('Error deleting chat:', deleteError);
      setError('حذف گفت‌وگو انجام نشد؛ دوباره تلاش کن.');
    }
  };

  const formatDate = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) return 'امروز';
    if (days === 1) return 'دیروز';
    if (days < 7) return `${days} روز پیش`;
    return date.toLocaleDateString('fa-IR');
  };

  // Show loading state while checking authentication
  if (authLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-gray-600 dark:text-gray-400">در حال بررسی احراز هویت...</div>
      </div>
    );
  }

  // Show unauthenticated message if user is not logged in
  if (!userInfo?.is_authenticated) {
    return <UnauthenticatedMessage />;
  }

  return (
    <div className="bg-home-v3 h-full min-h-0 overflow-y-auto px-3.5 pb-20 pt-[34px] app-sm:px-6 app-sm:pb-10 app-sm:pt-11 app-lg:px-9 app-lg:pt-[54px]">
      <div className="mx-auto max-w-[780px]">
        <section>
          <p className="flex items-center gap-2 text-[11px] font-bold text-ds-primary-strong app-sm:text-[13px]">
            <span className="h-0.5 w-6 rounded-full bg-ds-primary" aria-hidden="true" />
            دستیار هوشمند خرید باسلام
          </p>
          <h1 className="mt-3 text-[25px] font-black leading-[1.6] tracking-[-.4px] text-ds-ink app-sm:text-[30px] app-lg:text-4xl app-lg:leading-[1.55] app-lg:tracking-[-.8px]">
            سلام، برای خرید امروز چه کمکی از من می‌خوای؟
          </h1>
          <p className="mt-2 text-xs leading-7 text-ds-600 app-sm:text-sm app-sm:leading-8">
            محصول پیدا می‌کنم، گزینه‌ها رو کنار هم می‌ذارم و برای انتخاب مطمئن‌تر همراهت هستم.
          </p>
        </section>

        <form
          onSubmit={handleSubmit}
          className="mt-5 rounded-ds-12 border-2 border-ds-200 bg-ds-white p-1.5 shadow-ds-1 transition-[border-color,box-shadow] focus-within:border-ds-primary focus-within:shadow-[0_0_0_3px_rgb(var(--ds-primary-200))] app-sm:mt-[26px] app-sm:rounded-ds-16 app-sm:p-2"
        >
          <textarea
            ref={textareaRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit(e);
              }
            }}
            placeholder="مثلاً یه هدیه کاربردی برای خونه تا ۸۰۰ هزار تومن می‌خوام..."
            rows={3}
            className="block h-[94px] w-full resize-none border-0 bg-transparent px-3 py-2.5 text-sm leading-7 text-ds-ink outline-none placeholder:text-ds-400 app-sm:h-[88px] app-sm:text-[15px] app-sm:leading-7"
          />
          <div className="flex items-center gap-2 border-t border-ds-100 px-1 pt-2">
            <span className="hidden text-[11px] text-ds-500 app-sm:inline">Enter برای ارسال · Shift + Enter برای خط جدید</span>
            <Button type="submit" className="mr-auto" disabled={!inputValue.trim() || createChatMutation.isPending}>
              {createChatMutation.isPending ? <FiLoader className="animate-spin" size={18} /> : <FiSend className="rtl-flip" size={18} />}
              <span>{createChatMutation.isPending ? 'در حال ساخت گفت‌وگو' : 'شروع گفت‌وگو'}</span>
            </Button>
          </div>
        </form>

        <section className="mt-7">
          <div className="flex items-end justify-between gap-4">
            <h2 className="text-base font-bold text-ds-ink">می‌تونی از اینجا شروع کنی</h2>
            <span className="text-[11px] text-ds-500">شروع سریع</span>
          </div>
          {loadingPrompts ? (
            <div className="mt-3 flex min-h-16 items-center justify-center gap-2 rounded-ds-10 border border-ds-100 bg-ds-white text-xs text-ds-500">
              <FiLoader className="animate-spin" /> در حال بارگذاری پیشنهادها...
            </div>
          ) : starterPrompts.length > 0 && (
            <div>
              <div className="mt-3 grid grid-cols-1 gap-2.5 app-sm:grid-cols-2">
                {starterPrompts.slice(0, 4).map((prompt: any) => (
                  <button
                    key={prompt.id}
                    onClick={() => handlePromptClick(prompt.text)}
                    className="flex min-h-14 items-center gap-3 rounded-ds-10 border border-ds-100 bg-ds-white p-3 text-right text-[13px] leading-6 text-ds-800 transition-colors hover:border-ds-primary-muted hover:bg-ds-primary-soft app-sm:min-h-[62px]"
                  >
                    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-ds-8 bg-ds-subtle text-ds-700">{prompt.icon}</span>
                    <span className="flex-1">{prompt.text}</span>
                    <FiChevronLeft className="text-ds-400" size={17} />
                  </button>
                ))}
              </div>
              {promptHintMessage && (
                <div className="mt-2.5 animate-ds-fade-in rounded-ds-8 bg-ds-navy-soft px-3 py-2 text-[11px] leading-5 text-ds-700">
                  {promptHintMessage}
                </div>
              )}
            </div>
          )}
        </section>

        {!loading && !error && chats.length > 0 && (
          <section className="mt-7">
            <div className="flex items-end justify-between gap-4">
              <h2 className="text-base font-bold text-ds-ink">گفت‌وگوهای اخیر</h2>
              <small className="text-[11px] text-ds-500">{chats.length.toLocaleString('fa-IR')} گفت‌وگو</small>
            </div>
            <div className="mt-3 overflow-hidden rounded-ds-12 border border-ds-100 bg-ds-white">
              {chats.slice(0, 5).map((chat) => (
                <div key={chat.id} className="flex items-center border-b border-ds-100 last:border-b-0 hover:bg-ds-subtle">
                  <button onClick={() => navigate(`/chat/${chat.id}`)} className="flex min-w-0 flex-1 items-center gap-3 px-3 py-2.5 text-right">
                    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-ds-8 bg-ds-subtle text-ds-600"><FiMessageSquare size={17} /></span>
                    <span className="min-w-0 flex-1">
                      <strong className="block truncate text-xs text-ds-800">{chat.title}</strong>
                      <small className="mt-1 flex items-center gap-1 text-[10px] text-ds-500"><FiClock size={12} /> {formatDate(chat.timestamp)}</small>
                    </span>
                    <FiChevronLeft className="text-ds-400" size={18} />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeleteChat(chat.id)}
                    disabled={deleteChatMutation.isPending}
                    className="ml-2 grid h-9 w-9 shrink-0 place-items-center rounded-ds-6 text-ds-400 hover:bg-red-50 hover:text-ds-danger disabled:opacity-50"
                    aria-label={`حذف گفت‌وگوی ${chat.title}`}
                  >
                    <FiTrash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      {error && (
        <div className="fixed bottom-5 left-1/2 z-50 -translate-x-1/2 rounded-ds-8 bg-ds-danger px-4 py-2 text-xs text-white shadow-ds-3">
          {error}
        </div>
      )}
    </div>
  );
};

export default Home;
